/**
 * Landing 2.0 strings owned by W1-LAND-A: Appendix C rows 1 to 28 and 120 to
 * 141 of `.claude/briefs/spec-2.0-build-wave1.md`, ratified word for word
 * (Part 6, 2026-10-12). Each constant names its row. Bytes are the ratified
 * bytes: no edits for taste, no "fixes" of punctuation. W1-LAND-B's rows 29
 * to 119 live in `sections/`.
 *
 * Rows 136 ("Continue with Google"), 137 ("Sign in with Apple") and 138
 * ("Signing in…") are rendered by `ProviderButtons.tsx`, which is the one
 * implementation of the two provider buttons and already carries exactly
 * those bytes; they are pinned by `landing.v2.register.test.tsx`, not
 * repeated here.
 */

/** Row 1: the browser tab. */
export const PAGE_TITLE = 'Tayro: AI coaches for cross-country';
/** Row 2: the meta and share description. */
export const PAGE_DESCRIPTION =
  'Five AI coaches build your cross-country training plan around your race dates. Free to start.';

/** The five section ids. The sections themselves are W1-LAND-B's
 *  (`sections/index.ts`); the nav and the footer link to these ids. */
export const SECTION_IDS = ['what-it-does', 'coaches', 'parents', 'pricing', 'faq'] as const;
export type SectionId = (typeof SECTION_IDS)[number];

/** Rows 3 to 11: the nav. Link order is ratified. */
export const NAV_CONTENT = {
  wordmark: 'Tayro', // 3
  links: [
    { label: 'What it does', href: '#what-it-does' }, // 4
    { label: 'Coaches', href: '#coaches' }, // 5
    { label: 'For parents', href: '#parents' }, // 6
    { label: 'Pricing', href: '#pricing' }, // 7
    { label: 'FAQ', href: '#faq' }, // 8
  ],
  signIn: 'Sign in', // 9
  openMenu: 'Open menu', // 10
  closeMenu: 'Close menu', // 11
} as const;

export type Audience = 'runner' | 'coach' | 'parent';

/**
 * Which sheet an action opens. "parent" opens none: it scrolls to #parents. 'signin' (B412) is the sheet a Sign in
 * door opens (the nav's and the hero's): the same two providers, words for someone who already has an account.
 * 'getstarted' (founder 2026-10-21) is the sheet the paid Athlete plan's "Get started" opens in Pricing: the Start
 * free sentence under its own title.
 */
export type SheetKind = 'runner' | 'coach' | 'signin' | 'getstarted';

/**
 * B411 (founder 2026-10-19): a signed-out visitor on a deep link (races, plan, calendar, week, chat) is sent to
 * `SIGN_IN_LANDING_PATH`, and the 2.0 landing opens the sign-in sheet for the visit. One definition for both
 * sides: the routes navigate to the path, `routes/landing.tsx` reads `SIGN_IN_PARAM`.
 */
export const SIGN_IN_PARAM = 'signin';
export const SIGN_IN_LANDING_PATH = `/landing?${SIGN_IN_PARAM}=1`;

/** The words of row 13 that never split across lines (hero 1a, 2026-10-19). Not a row: the
 *  string of row 13 is unchanged; the hero wraps this run in a no-break unit. */
export const HEADLINE_NO_BREAK = 'cross-country,';

/** Rows 12 to 27: the hero. */
export const HERO_CONTENT = {
  badge: 'Now in early access', // 12
  headline: 'AI coaches for cross-country, built around your season.', // 13
  lead: 'Five AI coaches build your plan around your race dates and adjust it as your season goes.', // 14
  chooserLabel: 'Start here', // 15
  audiences: [
    {
      id: 'runner',
      label: 'I run cross-country', // 16
      next: 'Next: sign in with Google or Apple, answer a few questions about your running, then review your first week.', // 19
      action: 'Start free', // 20
    },
    {
      id: 'coach',
      label: 'I coach a team', // 17
      next: 'Next: sign in, create your team, then share an invite link with your runners.', // 21
      action: 'Set up your team', // 22
    },
    {
      id: 'parent',
      label: "I'm a parent", // 18
      next: 'What the AI coaches do, what a guardian approves, and who sees what.', // 23
      action: 'How it works for families', // 24
    },
  ],
  haveAccount: 'Already have an account?', // 25
  signIn: 'Sign in', // 26
  photoAlt: 'Runners on the start line of a cross-country race, team tents behind them', // 27
  appStoreBadge: 'Download on the App Store', // 28
} as const;

/** Rows 120 to 131: the footer. */
export const FOOTER_CONTENT = {
  tagline: 'AI coaches for cross-country.', // 120
  productTitle: 'Product', // 121
  productLinks: [
    { label: 'What it does', href: '#what-it-does' }, // 122
    { label: 'The coaches', href: '#coaches' }, // 123
    { label: 'Pricing', href: '#pricing' }, // 124
    { label: 'FAQ', href: '#faq' }, // 125
  ],
  familiesTitle: 'Families', // 126
  familiesLinks: [{ label: 'For parents and teams', href: '#parents' }], // 127
  companyTitle: 'Company', // 128
  contact: 'Contact', // 129
  contactHref: 'mailto:support@tayro.run',
  privacy: 'Privacy', // 130
  privacyHref: '/privacy',
  copyright: '© 2026 Tayro', // 131
} as const;

/** Rows 132 to 141: the sign-in sheet (phone) and dialog (desktop). */
export const SHEET_CONTENT = {
  runner: {
    title: 'Start free', // 132
    sentence: 'Sign in to start. Next, a few questions about your running.', // 133
  },
  coach: {
    title: 'Set up your team', // 134
    sentence: 'Sign in to start. Next, you name your team and get an invite link.', // 135
  },
  /** B412 (founder ruling 2026-10-19), after the spec table: the Sign in doors' sheet. Suggested rows 143, 144. */
  signin: {
    title: 'Sign in',
    sentence: 'Welcome back. Sign in with the account you used before.',
  },
  /** Founder ruling 2026-10-21, after the spec table: the paid plan's sheet. Row 145 (the sentence is row 133). */
  getstarted: {
    title: 'Get started',
    sentence: 'Sign in to start. Next, a few questions about your running.',
  },
  ageLine: 'Tayro is for runners 13 and over.', // 139
  privacy: 'Privacy', // 140
  privacyHref: '/privacy',
  close: 'Close', // 141
} as const;
